<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import {
  getProjeto,
  regenerarApiKeyProjeto,
  updateProjeto,
} from '@/api/projetos'
import { absoluteUrl, apiBaseUrl, apiPublicAssetUrl, getApiErrorMessage } from '@/api/client'
import type { AtualizarProjetoRequest, CampoMetadado, Projeto, ProjetoConfig } from '@/types/projetos'
import { formatSessaoBytes } from '@/utils/sessoes'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const route = useRoute()
const router = useRouter()

const projetoId = computed(() => {
  const { id } = route.params as { id?: string | string[] }
  return Array.isArray(id) ? (id[0] ?? '') : (id ?? '')
})

const projeto = ref<Projeto | null>(null)
const loading = ref(true)
const saving = ref(false)
const regenerating = ref(false)

const nome = ref('')
const slug = ref('')
const ativo = ref(true)
const allowedOriginsText = ref('')
const retencaoDias = ref<number | ''>('')
const camposMetadados = ref<CampoMetadado[]>([])
const apiKey = ref('')
const showNewKeyHint = ref(false)
const recorderBuildId = ref('')

const storageDriver = ref<'local' | 's3'>('local')
const storageLocalBasePath = ref('')
const s3Region = ref('')
const s3Bucket = ref('')
const s3Prefix = ref('')
const s3Endpoint = ref('')
const s3AccessKeyId = ref('')
const s3SecretAccessKey = ref('')
const s3Configured = ref(false)

const maxEventsPerRequest = ref(250)
const maxBodyBytes = ref(8388608)
const maxRequestsPerMinute = ref(180)

const samplingMousemove = ref(false)
const samplingScroll = ref(150)
const samplingInput = ref<'last' | 'all'>('last')
const samplingMedia = ref(800)

const requireConsent = ref(false)
const allowPublicShare = ref(true)
const showRecordingIndicator = ref(false)
const blockClassesText = ref('')
const maskClassesText = ref('')
const maskTextClassesText = ref('')
const blockSelector = ref('')
const maskSelector = ref('')
const maskTextSelector = ref('')
const blockedPathPatternsText = ref('')

const webhookUrl = ref('')
const webhookSecret = ref('')
const webhookErrorThreshold = ref(5)

const originsVazio = computed(() => allowedOriginsText.value.trim() === '')

const STALE_DAYS = 7

const semGravacaoRecente = computed(() => {
  if (!projeto.value?.ultima_sessao_em) {
    return true
  }
  const last = new Date(projeto.value.ultima_sessao_em.replace(' ', 'T'))
  if (Number.isNaN(last.getTime())) {
    return true
  }
  const cutoff = Date.now() - STALE_DAYS * 24 * 60 * 60 * 1000
  return last.getTime() < cutoff
})

const errorRateLabel = computed(() => {
  const total = projeto.value?.sessoes_count ?? 0
  const errors = projeto.value?.error_events_count ?? 0
  if (total === 0 || errors === 0) {
    return null
  }
  const rate = Math.round((errors / total) * 10) / 10
  return `${errors} erro(s) · ${rate} por sessão`
})

const recorderScriptUrl = computed(() => {
  const q = recorderBuildId.value ? `?v=${encodeURIComponent(recorderBuildId.value)}` : ''
  return `${recorderUrl.value}${q}`
})

const recorderUrl = computed(() => absoluteUrl(apiPublicAssetUrl('recorder.js')))

const embedSnippet = computed(() => {
  if (!apiKey.value) return ''
  return `<script src="${recorderScriptUrl.value}"><\/script>
<script>
  SessionRecorder.init({
    apiUrl: '${absoluteUrl(apiBaseUrl())}',
    projectKey: '${apiKey.value}',
    usuario: { id: 'USER_ID', nome: 'Nome do usuário' },
    metadados: { loja: 'Centro' }
  })
<\/script>`
})

function linesToArray(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function arrayToLines(values: string[]): string {
  return values.join('\n')
}

function fillConfigForm(config: ProjetoConfig | undefined): void {
  const cfg = config
  storageDriver.value = cfg?.storage?.driver ?? 'local'
  storageLocalBasePath.value = cfg?.storage?.local?.base_path ?? ''
  s3Region.value = cfg?.storage?.s3?.region ?? ''
  s3Bucket.value = cfg?.storage?.s3?.bucket ?? ''
  s3Prefix.value = cfg?.storage?.s3?.prefix ?? ''
  s3Endpoint.value = cfg?.storage?.s3?.endpoint ?? ''
  s3AccessKeyId.value = cfg?.storage?.s3?.access_key_id ?? ''
  s3SecretAccessKey.value = ''
  s3Configured.value = cfg?.storage?.s3?.s3_configured ?? false

  maxEventsPerRequest.value = cfg?.ingest_limits?.max_events_per_request ?? 250
  maxBodyBytes.value = cfg?.ingest_limits?.max_body_bytes ?? 8388608
  maxRequestsPerMinute.value = cfg?.ingest_limits?.max_requests_per_minute ?? 180

  samplingMousemove.value = cfg?.sampling?.mousemove ?? false
  samplingScroll.value = cfg?.sampling?.scroll ?? 150
  samplingInput.value = cfg?.sampling?.input === 'all' ? 'all' : 'last'
  samplingMedia.value = cfg?.sampling?.media ?? 800

  requireConsent.value = cfg?.privacy?.require_consent ?? false
  allowPublicShare.value = cfg?.privacy?.allow_public_share ?? true
  showRecordingIndicator.value = cfg?.privacy?.show_recording_indicator ?? false
  blockClassesText.value = arrayToLines(cfg?.privacy?.block_classes ?? [])
  maskClassesText.value = arrayToLines(cfg?.privacy?.mask_classes ?? [])
  maskTextClassesText.value = arrayToLines(cfg?.privacy?.mask_text_classes ?? [])
  blockSelector.value = cfg?.privacy?.block_selector ?? ''
  maskSelector.value = cfg?.privacy?.mask_selector ?? ''
  maskTextSelector.value = cfg?.privacy?.mask_text_selector ?? ''
  blockedPathPatternsText.value = arrayToLines(cfg?.privacy?.blocked_path_patterns ?? [])

  webhookUrl.value = cfg?.webhook?.url ?? ''
  webhookSecret.value = ''
  webhookErrorThreshold.value = cfg?.webhook?.error_threshold ?? 5
}

function buildConfigPatch(): AtualizarProjetoRequest['config'] {
  const s3Patch: NonNullable<NonNullable<AtualizarProjetoRequest['config']>['storage']>['s3'] = {
    region: s3Region.value.trim() || null,
    bucket: s3Bucket.value.trim() || null,
    prefix: s3Prefix.value.trim() || null,
    endpoint: s3Endpoint.value.trim() || null,
    access_key_id: s3AccessKeyId.value.trim() || null,
  }
  if (s3SecretAccessKey.value.trim() !== '') {
    s3Patch.secret_access_key = s3SecretAccessKey.value.trim()
  }

  const webhookPatch: NonNullable<AtualizarProjetoRequest['config']>['webhook'] = {
    url: webhookUrl.value.trim() || null,
    error_threshold: Number(webhookErrorThreshold.value),
  }
  if (webhookSecret.value.trim() !== '') {
    webhookPatch.secret = webhookSecret.value.trim()
  }

  return {
    storage: {
      driver: storageDriver.value,
      local: { base_path: storageLocalBasePath.value.trim() || null },
      s3: s3Patch,
    },
    ingest_limits: {
      max_events_per_request: Number(maxEventsPerRequest.value),
      max_body_bytes: Number(maxBodyBytes.value),
      max_requests_per_minute: Number(maxRequestsPerMinute.value),
    },
    sampling: {
      mousemove: samplingMousemove.value,
      scroll: Number(samplingScroll.value),
      input: samplingInput.value,
      media: Number(samplingMedia.value),
    },
    privacy: {
      require_consent: requireConsent.value,
      allow_public_share: allowPublicShare.value,
      show_recording_indicator: showRecordingIndicator.value,
      block_classes: linesToArray(blockClassesText.value),
      mask_classes: linesToArray(maskClassesText.value),
      mask_text_classes: linesToArray(maskTextClassesText.value),
      block_selector: blockSelector.value.trim(),
      mask_selector: maskSelector.value.trim(),
      mask_text_selector: maskTextSelector.value.trim(),
      blocked_path_patterns: linesToArray(blockedPathPatternsText.value),
    },
    webhook: webhookPatch,
  }
}

function fillForm(data: Projeto): void {
  nome.value = data.nome
  slug.value = data.slug
  ativo.value = data.ativo
  allowedOriginsText.value = data.allowed_origins.join('\n')
  retencaoDias.value = data.retencao_dias ?? ''
  camposMetadados.value = data.campos_metadados.map((c) => ({ omitir: false, ...c }))
  apiKey.value = data.api_key ?? apiKey.value
  fillConfigForm(data.config)
}

async function loadRecorderVersion(): Promise<void> {
  try {
    const response = await fetch(apiPublicAssetUrl('recorder.version'))
    if (!response.ok) {
      return
    }
    const text = (await response.text()).trim()
    if (text && !text.startsWith('<')) {
      recorderBuildId.value = text
    }
  } catch {
    recorderBuildId.value = ''
  }
}

async function load(): Promise<void> {
  loading.value = true
  try {
    await loadRecorderVersion()
    const data = await getProjeto(projetoId.value)
    projeto.value = data
    fillForm(data)
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Projeto não encontrado',
      text: getApiErrorMessage(error, 'Erro ao carregar projeto'),
    })
    await router.push('/admin/projetos')
  } finally {
    loading.value = false
  }
}

function parseOrigins(): string[] {
  return allowedOriginsText.value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function addCampo(): void {
  camposMetadados.value.push({ chave: '', rotulo: '', omitir: false })
}

function removeCampo(index: number): void {
  camposMetadados.value.splice(index, 1)
}

async function submit(): Promise<void> {
  saving.value = true
  try {
    const updated = await updateProjeto(projetoId.value, {
      nome: nome.value,
      slug: slug.value,
      ativo: ativo.value,
      allowed_origins: parseOrigins(),
      retencao_dias: retencaoDias.value === '' ? null : Number(retencaoDias.value),
      campos_metadados: camposMetadados.value.filter((c) => c.chave.trim() !== ''),
      config: buildConfigPatch(),
    })
    projeto.value = updated
    fillForm(updated)
    await Swal.fire({ icon: 'success', title: 'Projeto atualizado', timer: 1500, showConfirmButton: false })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Não foi possível salvar',
      text: getApiErrorMessage(error, 'Erro ao atualizar projeto'),
    })
  } finally {
    saving.value = false
  }
}

async function regenerarKey(): Promise<void> {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Regenerar API key?',
    text: 'Sites que usam a chave atual deixarão de gravar até atualizar o snippet.',
    showCancelButton: true,
    confirmButtonText: 'Regenerar',
    cancelButtonText: 'Cancelar',
  })

  if (!result.isConfirmed) return

  regenerating.value = true
  try {
    const updated = await regenerarApiKeyProjeto(projetoId.value)
    apiKey.value = updated.api_key ?? ''
    showNewKeyHint.value = true
    await Swal.fire({ icon: 'success', title: 'Nova API key gerada' })
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: getApiErrorMessage(error, 'Não foi possível regenerar a chave'),
    })
  } finally {
    regenerating.value = false
  }
}

async function copySnippet(): Promise<void> {
  try {
    await navigator.clipboard.writeText(embedSnippet.value)
    await Swal.fire({ icon: 'success', title: 'Snippet copiado', timer: 1200, showConfirmButton: false })
  } catch {
    await Swal.fire({ icon: 'info', title: 'Copie o snippet manualmente' })
  }
}

onMounted(() => {
  const state = history.state as { apiKey?: string }
  if (state?.apiKey) {
    apiKey.value = state.apiKey
    showNewKeyHint.value = true
  }
  void load()
})
</script>

<template>
  <AdminLayout>
    <template #title>
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <h1 class="h5 mb-0">{{ projeto?.nome ?? 'Projeto' }}</h1>
        <RouterLink to="/admin/projetos" class="btn btn-outline-secondary btn-sm">Voltar</RouterLink>
      </div>
    </template>

    <div v-if="loading" class="text-muted">Carregando…</div>

    <template v-else>
      <div class="card shadow-sm border-0 p-4 mb-4">
        <h2 class="h6 mb-3">Instalação</h2>
        <div v-if="originsVazio" class="alert alert-warning small py-2">
          Em produção, cadastre as origens do site. Lista vazia aceita qualquer origem (CORS).
        </div>
        <div v-if="semGravacaoRecente" class="alert alert-warning small py-2">
          Sem gravação recente{{ projeto?.ultima_sessao_em ? ` (última há mais de ${STALE_DAYS} dias)` : '' }}.
        </div>
        <div v-if="projeto?.sessoes_count != null" class="small text-muted mb-3">
          <div>
            {{ projeto.sessoes_count }} sessão(ões) gravada(s)
            <span v-if="projeto.sessoes_hoje != null"> · {{ projeto.sessoes_hoje }} hoje</span>
          </div>
          <div v-if="projeto.storage_bytes != null">
            Storage: {{ formatSessaoBytes(projeto.storage_bytes) }}
          </div>
          <div v-if="errorRateLabel">{{ errorRateLabel }}</div>
          <div v-if="projeto.ultima_sessao_em">Última sessão: {{ projeto.ultima_sessao_em }}</div>
        </div>
        <div v-if="showNewKeyHint && apiKey" class="alert alert-warning small py-2">
          Nova API key gerada. Atualize o snippet nos sites que usam este projeto.
        </div>
        <div class="mb-2">
          <label class="form-label small text-muted">API key</label>
          <div class="input-group">
            <input :value="apiKey" type="text" class="form-control font-monospace" readonly>
            <button type="button" class="btn btn-outline-warning" :disabled="regenerating" @click="regenerarKey">
              Regenerar
            </button>
          </div>
        </div>
        <label class="form-label small text-muted">Snippet</label>
        <pre class="bg-dark text-light p-3 rounded small mb-2" style="white-space: pre-wrap">{{ embedSnippet }}</pre>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="copySnippet">
          Copiar snippet
        </button>
      </div>

      <form class="card shadow-sm border-0 p-4" @submit.prevent="submit">
        <div class="mb-3">
          <label class="form-label" for="nome">Nome</label>
          <input id="nome" v-model="nome" type="text" class="form-control" required :disabled="saving">
        </div>

        <div class="mb-3">
          <label class="form-label" for="slug">Slug</label>
          <input id="slug" v-model="slug" type="text" class="form-control" required :disabled="saving">
        </div>

        <div class="form-check mb-3">
          <input id="ativo" v-model="ativo" class="form-check-input" type="checkbox" :disabled="saving">
          <label class="form-check-label" for="ativo">Projeto ativo</label>
        </div>

        <div class="mb-3">
          <label class="form-label" for="origins">Origens permitidas</label>
          <div v-if="originsVazio" class="alert alert-warning small py-2 mb-2">
            Em produção, cadastre as origens do site. Lista vazia aceita qualquer origem (CORS).
          </div>
          <textarea id="origins" v-model="allowedOriginsText" class="form-control font-monospace" rows="3" :disabled="saving" />
        </div>

        <div class="mb-3">
          <label class="form-label" for="retencao">Retenção (dias)</label>
          <input id="retencao" v-model="retencaoDias" type="number" min="1" class="form-control" :disabled="saving">
        </div>

        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="form-label mb-0">Campos de metadados</label>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="saving" @click="addCampo">
              Adicionar campo
            </button>
          </div>
          <div v-for="(campo, index) in camposMetadados" :key="index" class="row g-2 mb-2 align-items-center">
            <div class="col-md-4">
              <input v-model="campo.chave" type="text" class="form-control" placeholder="chave (ex: loja)" :disabled="saving">
            </div>
            <div class="col-md-4">
              <input v-model="campo.rotulo" type="text" class="form-control" placeholder="rótulo (ex: Loja)" :disabled="saving">
            </div>
            <div class="col-md-2">
              <div class="form-check">
                <input :id="`omitir-${index}`" v-model="campo.omitir" class="form-check-input" type="checkbox" :disabled="saving">
                <label class="form-check-label small" :for="`omitir-${index}`">Omitir</label>
              </div>
            </div>
            <div class="col-md-2">
              <button type="button" class="btn btn-outline-danger w-100" :disabled="saving" @click="removeCampo(index)">
                Remover
              </button>
            </div>
          </div>
        </div>

        <hr class="my-4">

        <h2 class="h6 mb-3">Storage</h2>
        <div class="mb-3">
          <label class="form-label" for="storage_driver">Driver</label>
          <select id="storage_driver" v-model="storageDriver" class="form-select" :disabled="saving">
            <option value="local">Local</option>
            <option value="s3">S3</option>
          </select>
        </div>

        <div v-if="storageDriver === 'local'" class="mb-4">
          <label class="form-label" for="storage_local_path">Base path (opcional)</label>
          <input id="storage_local_path" v-model="storageLocalBasePath" type="text" class="form-control font-monospace" :disabled="saving">
        </div>

        <div v-else class="mb-4">
          <div v-if="s3Configured" class="alert alert-info small py-2">Credenciais S3 já configuradas. Deixe em branco para manter.</div>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label" for="s3_region">Region</label>
              <input id="s3_region" v-model="s3Region" type="text" class="form-control" :disabled="saving">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="s3_bucket">Bucket</label>
              <input id="s3_bucket" v-model="s3Bucket" type="text" class="form-control" :disabled="saving">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="s3_prefix">Prefix</label>
              <input id="s3_prefix" v-model="s3Prefix" type="text" class="form-control" :disabled="saving">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="s3_endpoint">Endpoint (opcional)</label>
              <input id="s3_endpoint" v-model="s3Endpoint" type="text" class="form-control" :disabled="saving">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="s3_access_key">Access key ID</label>
              <input id="s3_access_key" v-model="s3AccessKeyId" type="text" class="form-control font-monospace" :disabled="saving">
            </div>
            <div class="col-md-6">
              <label class="form-label" for="s3_secret">Secret access key</label>
              <input id="s3_secret" v-model="s3SecretAccessKey" type="password" class="form-control" :disabled="saving" placeholder="Deixe em branco para manter">
            </div>
          </div>
        </div>

        <hr class="my-4">

        <h2 class="h6 mb-3">Limites de ingestão</h2>
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <label class="form-label" for="max_events">Máx. eventos por request</label>
            <input id="max_events" v-model.number="maxEventsPerRequest" type="number" min="1" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="max_body">Máx. body (bytes)</label>
            <input id="max_body" v-model.number="maxBodyBytes" type="number" min="1" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="max_rpm">Máx. requests/min</label>
            <input id="max_rpm" v-model.number="maxRequestsPerMinute" type="number" min="1" class="form-control" :disabled="saving">
          </div>
        </div>

        <hr class="my-4">

        <h2 class="h6 mb-3">Sampling (recorder)</h2>
        <div class="form-check mb-3">
          <input id="sampling_mousemove" v-model="samplingMousemove" class="form-check-input" type="checkbox" :disabled="saving">
          <label class="form-check-label" for="sampling_mousemove">Gravar movimento do mouse</label>
        </div>
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <label class="form-label" for="sampling_scroll">Scroll (ms)</label>
            <input id="sampling_scroll" v-model.number="samplingScroll" type="number" min="0" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="sampling_media">Media (ms)</label>
            <input id="sampling_media" v-model.number="samplingMedia" type="number" min="0" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="sampling_input">Input</label>
            <select id="sampling_input" v-model="samplingInput" class="form-select" :disabled="saving">
              <option value="last">last</option>
              <option value="all">all</option>
            </select>
          </div>
        </div>

        <hr class="my-4">

        <h2 class="h6 mb-3">Privacidade (recorder)</h2>
        <div class="form-check mb-2">
          <input id="require_consent" v-model="requireConsent" class="form-check-input" type="checkbox" :disabled="saving">
          <label class="form-check-label" for="require_consent">Exigir consentimento</label>
        </div>
        <div class="form-check mb-2">
          <input id="allow_public_share" v-model="allowPublicShare" class="form-check-input" type="checkbox" :disabled="saving">
          <label class="form-check-label" for="allow_public_share">Permitir compartilhamento público</label>
        </div>
        <div class="form-check mb-3">
          <input id="show_recording_indicator" v-model="showRecordingIndicator" class="form-check-input" type="checkbox" :disabled="saving">
          <label class="form-check-label" for="show_recording_indicator">Mostrar indicador “Gravando”</label>
        </div>
        <div class="row g-3 mb-4">
          <div class="col-md-6">
            <label class="form-label" for="block_classes">Classes bloqueadas (1 por linha)</label>
            <textarea id="block_classes" v-model="blockClassesText" class="form-control font-monospace" rows="3" :disabled="saving" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="mask_classes">Classes mascaradas (1 por linha)</label>
            <textarea id="mask_classes" v-model="maskClassesText" class="form-control font-monospace" rows="3" :disabled="saving" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="mask_text_classes">Classes com texto mascarado</label>
            <textarea id="mask_text_classes" v-model="maskTextClassesText" class="form-control font-monospace" rows="3" :disabled="saving" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="blocked_paths">Padrões de path bloqueados</label>
            <textarea id="blocked_paths" v-model="blockedPathPatternsText" class="form-control font-monospace" rows="3" :disabled="saving" />
          </div>
          <div class="col-md-4">
            <label class="form-label" for="block_selector">CSS block_selector</label>
            <input id="block_selector" v-model="blockSelector" type="text" class="form-control font-monospace" :disabled="saving" placeholder="#secret, .pii">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="mask_selector">CSS mask_selector</label>
            <input id="mask_selector" v-model="maskSelector" type="text" class="form-control font-monospace" :disabled="saving" placeholder="[data-mask]">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="mask_text_selector">CSS mask_text_selector</label>
            <input id="mask_text_selector" v-model="maskTextSelector" type="text" class="form-control font-monospace" :disabled="saving" placeholder=".user-name">
          </div>
        </div>

        <hr class="my-4">

        <h2 class="h6 mb-3">Webhook de erros</h2>
        <div class="row g-3 mb-4">
          <div class="col-md-8">
            <label class="form-label" for="webhook_url">URL</label>
            <input id="webhook_url" v-model="webhookUrl" type="url" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-4">
            <label class="form-label" for="webhook_threshold">Limite de erros</label>
            <input id="webhook_threshold" v-model.number="webhookErrorThreshold" type="number" min="1" class="form-control" :disabled="saving">
          </div>
          <div class="col-md-8">
            <label class="form-label" for="webhook_secret">Secret</label>
            <input id="webhook_secret" v-model="webhookSecret" type="password" class="form-control" :disabled="saving" placeholder="Deixe em branco para manter">
          </div>
        </div>

        <button type="submit" class="btn btn-primary" :disabled="saving">Salvar alterações</button>
      </form>
    </template>
  </AdminLayout>
</template>
